import * as React from 'react';

import {Button} from 'shells/button/button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Divider} from 'shells/layout/divider';
import {Stack} from 'shells/layout/stack/stack';
import {Sidebar} from 'shells/sidebar/sidebar';
import {Body} from 'shells/typography/body';

import type {
    GetMoneyPage_maUrl_automation_actions as AutomationAction,
    ActionInput,
} from 'nutshell-graphql-types';

import {NotifiableSelectList} from './selects/notifiable-select-list';
import type {SelectedNotifiable} from './selects/notifiable-select-list';

type Props = {
    existingAutomationAction: AutomationAction | null | undefined;
    onClose: () => void;
    handleSave: (automationActionToSave: ActionInput) => void;
    onDelete: (automationActionToDelete: AutomationAction) => Promise<any> | null | undefined;
    isSaving: boolean;
    saveFailedErrorMessage: string | null | undefined;
};

function getAliasLabel(type: string): string {
    if (type === 'CONTACT_ASSIGNEE') {
        return 'Person assignee';
    }
    if (type === 'RELATED_LEAD_ASSIGNEE') {
        return 'Related lead assignee';
    }

    return type;
}

function getInitialNotifiables(existingAction?: AutomationAction | null): SelectedNotifiable[] {
    if (
        existingAction &&
        existingAction.config.__typename === 'ActionConfigSendPageViewedNotification'
    ) {
        const notifiables: SelectedNotifiable[] = [];
        for (const notifiable of existingAction.config.notifiables) {
            if (notifiable.__typename === 'User') {
                notifiables.push({
                    id: notifiable.id,
                    label: notifiable.name,
                    isAlias: false,
                });
            } else if (notifiable.__typename === 'NotifiableAlias') {
                notifiables.push({
                    id: notifiable.type,
                    label: getAliasLabel(notifiable.type),
                    isAlias: true,
                });
            }
        }

        return notifiables;
    }

    return [];
}

export function CreateEditNotificationAutomationSidebarForm(props: Props) {
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const [automationAction, setAutomationAction] = React.useState<ActionInput | null | undefined>(
        null
    );
    const [selectedNotifiables, setSelectedNotifiables] = React.useState<SelectedNotifiable[]>(
        getInitialNotifiables(props.existingAutomationAction)
    );

    React.useEffect(() => {
        if (selectedNotifiables.length === 0) {
            setAutomationAction(null);

            return;
        }

        // Combine user IDs and alias types into a single array for the API
        const notifiableIds = selectedNotifiables.map((n) => n.id);

        setAutomationAction({
            config: {
                notifiables: notifiableIds,
            },
        });
    }, [selectedNotifiables]);

    return (
        <Sidebar
            isOpen={true}
            // @ts-expect-error
            headerTitle={props.onDelete ? 'Edit action' : 'Add an action'}
            preventBodyScroll={false}
            canClickOutsideToClose={false}
            canEscapeToClose={true}
            onClose={props.onClose}
            onSave={() => {
                if (!automationAction) {
                    return;
                }
                props.handleSave(automationAction);
            }}
            isSaving={props.isSaving}
            submitDisabled={props.isSaving || !automationAction}
            customPositionAttributes={{
                display: 'flex',
                flexDirection: 'column',
                right: 0,
                top: 0,
            }}
        >
            {props.saveFailedErrorMessage ? (
                <FlashBanner type='error' leftAlignContent={true}>
                    {props.saveFailedErrorMessage}
                </FlashBanner>
            ) : undefined}
            <div className='full-height flex flex-dir-col overflow-auto'>
                <div className='flex justify-center pad-16'>
                    <Body size='small' color='light' isFullWidth={true}>
                        Notifications will automatically send when a known visitor lands on this
                        webpage
                    </Body>
                </div>
                <div className='ml-16 mr-16'>
                    <Divider variant='line--horizontal' />
                </div>
                <div className='flex flex-dir-col justify-sb flex-grow'>
                    <div className='flex flex-dir-col ml-16 mr-16 mt-16 gap-24'>
                        <Stack spacing={6}>
                            <Body>Who should receive this notification?</Body>
                            <NotifiableSelectList
                                selectedNotifiables={selectedNotifiables}
                                onChange={setSelectedNotifiables}
                            />
                        </Stack>
                    </div>
                    {/* @ts-expect-error */}
                    {props.onDelete ? (
                        <div className='full-width flex justify-end pad-16 border-box'>
                            <Button
                                onClick={() => {
                                    setIsDeleting(true);
                                }}
                                variant='danger'
                                size='big'
                                isFullWidth={true}
                            >
                                {isDeleting ? 'Removing…' : 'Delete'}
                            </Button>
                        </div>
                    ) : undefined}
                </div>
                {isDeleting && props.onDelete ? (
                    <ConfirmationDialog
                        title='Are you sure you want to delete this automation?'
                        message='This action cannot be undone.'
                        confirmText='Delete'
                        cancelText='Cancel'
                        onCancel={() => {
                            setIsDeleting(false);
                        }}
                        onConfirm={() => {
                            setIsDeleting(false);
                            if (!props.existingAutomationAction || !props.onDelete) {
                                return;
                            }
                            props.onDelete(props.existingAutomationAction);
                        }}
                        isConfirmDestructive={true}
                    />
                ) : undefined}
            </div>
        </Sidebar>
    );
}
